import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';

export interface DriftDashboard {
  total_deployments: number;
  by_status: Record<string, number>;
  by_service: Record<string, { total: number; in_sync: number; drifted: number }>;
}

export interface DeploymentMapCustomer {
  customer_id: string;
  name: string;
  deployments: Record<string, string>;
}

export interface DeploymentMap {
  products: string[];
  customers: DeploymentMapCustomer[];
}

export interface ActivityEntry {
  timestamp: string;
  customer_id: string;
  deployment_id: string;
  action: string;
  product: string;
  service: string;
  actor: string;
  result: string;
}

export interface ActivityLog {
  activities: ActivityEntry[];
  total_count: number;
}

export interface ActivityLogFilters {
  customer_id?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export interface OperationsService {
  getDriftDashboard(): Promise<DriftDashboard>;
  getDeploymentMap(): Promise<DeploymentMap>;
  getActivityLog(filters?: ActivityLogFilters): Promise<ActivityLog>;
}

export const createOperationsService = (idToken: string): OperationsService => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    getDriftDashboard: () =>
      rpcCall(client, 'operations.GetDriftDashboard'),

    getDeploymentMap: () =>
      rpcCall(client, 'operations.GetDeploymentMap'),

    getActivityLog: (filters?: ActivityLogFilters) =>
      rpcCall(client, 'operations.GetActivityLog', (filters || {}) as Record<string, unknown>),
  };
};

export const useOperationsService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    return createOperationsService(idToken);
  }, [idToken]);
};
